import type { ManageBreadcrumbNavigationUseCase } from '@/application/navigation';
import type { NavigationStateEntity } from '@/domains/navigation/navigation.entity';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Hook personalizado para manejar la navegación de breadcrumbs
 * Implementa SRP: Solo se encarga de coordinar la UI con los use cases
 * Sigue DIP: Depende de abstracciones (use case) no de implementaciones concretas
 */
export const useBreadcrumbNavigation = (
  manageBreadcrumbNavigationUseCase: ManageBreadcrumbNavigationUseCase,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Agrega una nueva capa de filtro al breadcrumb
   */
  const addFilterLayer = useCallback(
    (title: string, path: string, params?: Record<string, string>) => {
      try {
        return manageBreadcrumbNavigationUseCase.addFilterLayer(
          title,
          path,
          params,
        );
      } catch (error) {
        console.error('Error agregando capa de filtro:', error);
        throw error;
      }
    },
    [manageBreadcrumbNavigationUseCase],
  );

  /**
   * Navega hacia atrás hasta un nivel específico
   */
  const navigateBackToIndex = useCallback(
    (targetIndex: number) => {
      const updatedState =
        manageBreadcrumbNavigationUseCase.navigateBack(targetIndex);
      const targetBreadcrumb = updatedState.breadcrumbs[targetIndex];

      if (targetBreadcrumb) {
        navigate(targetBreadcrumb.path);
      } else {
        navigate('/');
      }

      return updatedState;
    },
    [manageBreadcrumbNavigationUseCase, navigate],
  );

  /**
   * Limpia toda la navegación y vuelve al inicio
   */
  const resetNavigation = useCallback(() => {
    manageBreadcrumbNavigationUseCase.resetNavigation();
    navigate('/');
  }, [manageBreadcrumbNavigationUseCase, navigate]);

  /**
   * Obtiene el estado actual de navegación
   */
  const getCurrentNavigation = useCallback((): NavigationStateEntity => {
    return manageBreadcrumbNavigationUseCase.getCurrentNavigation();
  }, [manageBreadcrumbNavigationUseCase]);

  /**
   * Solo se encarga de limpiar y no de navegar
   */
  const clearBreadcrumbs = useCallback(() => {
    manageBreadcrumbNavigationUseCase.resetNavigation();
  }, [manageBreadcrumbNavigationUseCase]);

  return {
    addFilterLayer,
    navigateBackToIndex,
    resetNavigation,
    clearBreadcrumbs,
    getCurrentNavigation,
    currentPath: location.pathname,
  };
};
